import { create } from 'zustand';

export type ScoringStatus = 'idle' | 'evaluating' | 'verified' | 'purged';

export interface ClinicEvaluationData {
  revenueMln: number;
  ebitdaMln: number;
  clinicType: 'multidisciplinary' | 'dentistry' | 'cosmetology' | 'surgery' | 'diagnostics';
  location: string;
  hasSubdivisionTaxRisk: boolean; // ст. 54.1 НК РФ
  hasVerbalLease: boolean;
  hasKeyDoctorDependency: boolean;
  hasUnlicensedSoftware: boolean;
  cadastralStatus: 'owned' | 'long_lease' | 'short_lease';
}

export interface SessionState {
  // Zero-Trace In-Memory Session Info
  sessionToken: string;
  nodeId: string;
  encryptionStandard: string;
  sessionStartedAt: number;
  lastActiveTimestamp: number;
  isSessionActive: boolean;
  purgeCount: number;
  lastPurgeReason: string | null;

  // Scoring & M&A Calculator State
  scoringStatus: ScoringStatus;
  evaluationData: ClinicEvaluationData;
  calculatedValuationMln: {
    asIs: number;
    withRemediation: number;
    institutionalPotential: number;
    deltaMln: number;
  };

  // UI state
  activeGlossaryTerm: string | null;
  activeGateStep: number;
  isNdaModalOpen: boolean;

  // Actions (Strictly in-memory mutations)
  updateActivity: () => void;
  setScoringStatus: (status: ScoringStatus) => void;
  setEvaluationData: (data: Partial<ClinicEvaluationData>) => void;
  setActiveGlossaryTerm: (term: string | null) => void;
  setActiveGateStep: (step: number) => void;
  setNdaModalOpen: (open: boolean) => void;
  recalculateValuation: () => void;
  destroySession: (reason?: string) => void;
}

const generateEphemeralToken = () => {
  const chars = '0123456789ABCDEF';
  let result = 'SP-RAM-';
  for (let i = 0; i < 8; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
};

const initialEvaluationData: ClinicEvaluationData = {
  revenueMln: 160,
  ebitdaMln: 38,
  clinicType: 'multidisciplinary',
  location: 'Москва (ЦАО / ЮЗАО)',
  hasSubdivisionTaxRisk: true,
  hasVerbalLease: false,
  hasKeyDoctorDependency: true,
  hasUnlicensedSoftware: true,
  cadastralStatus: 'long_lease',
};

const calculateMultipliers = (data: ClinicEvaluationData) => {
  const ebitda = data.ebitdaMln;
  // Baseline unmitigated multiple: 4.0x
  let asIsMultiple = 4.0;
  if (data.hasSubdivisionTaxRisk) asIsMultiple -= 0.6;
  if (data.hasKeyDoctorDependency) asIsMultiple -= 0.4;
  if (data.hasVerbalLease) asIsMultiple -= 0.5;
  if (data.hasUnlicensedSoftware) asIsMultiple -= 0.3;
  asIsMultiple = Math.max(2.2, asIsMultiple);

  // Sanitized with S&P remediation multiple: 6.0x
  const remediationMultiple = 6.0;

  // Institutional target (S&P Clinic OS™ + Roll-up candidate): 7.5x
  const institutionalMultiple = 7.5;

  const asIs = Math.round(ebitda * asIsMultiple);
  const withRemediation = Math.round(ebitda * remediationMultiple);
  const institutionalPotential = Math.round(ebitda * institutionalMultiple);
  const deltaMln = institutionalPotential - asIs;

  return {
    asIs,
    withRemediation,
    institutionalPotential,
    deltaMln,
  };
};

export const useSessionStore = create<SessionState>((set, get) => {
  const initialCalculations = calculateMultipliers(initialEvaluationData);

  return {
    sessionToken: generateEphemeralToken(),
    nodeId: 'MSK-C2 (Direct Link)',
    encryptionStandard: 'AES-256-GCM / In-Memory RAM',
    sessionStartedAt: Date.now(),
    lastActiveTimestamp: Date.now(),
    isSessionActive: true,
    purgeCount: 0,
    lastPurgeReason: null,

    scoringStatus: 'idle',
    evaluationData: initialEvaluationData,
    calculatedValuationMln: initialCalculations,

    activeGlossaryTerm: null,
    activeGateStep: 1,
    isNdaModalOpen: false,

    updateActivity: () => {
      set({ lastActiveTimestamp: Date.now() });
    },

    setScoringStatus: (status) => {
      set({ scoringStatus: status, lastActiveTimestamp: Date.now() });
    },

    setEvaluationData: (partial) => {
      set((state) => {
        const updated = { ...state.evaluationData, ...partial };
        return {
          evaluationData: updated,
          calculatedValuationMln: calculateMultipliers(updated),
          lastActiveTimestamp: Date.now(),
        };
      });
    },

    setActiveGlossaryTerm: (term) => {
      set({ activeGlossaryTerm: term, lastActiveTimestamp: Date.now() });
    },

    setActiveGateStep: (step) => {
      set({ activeGateStep: step, lastActiveTimestamp: Date.now() });
    },

    setNdaModalOpen: (open) => {
      set({ isNdaModalOpen: open, lastActiveTimestamp: Date.now() });
    },

    recalculateValuation: () => {
      const { evaluationData } = get();
      set({
        calculatedValuationMln: calculateMultipliers(evaluationData),
        lastActiveTimestamp: Date.now(),
      });
    },

    // Zero-Trace Purge Protocol: wipes state cleanly from in-memory RAM
    destroySession: (reason = 'Security Purge Triggered') => {
      const newCalculations = calculateMultipliers(initialEvaluationData);
      set((state) => ({
        sessionToken: generateEphemeralToken(),
        sessionStartedAt: Date.now(),
        lastActiveTimestamp: Date.now(),
        isSessionActive: true,
        purgeCount: state.purgeCount + 1,
        lastPurgeReason: reason,
        scoringStatus: 'purged',
        evaluationData: { ...initialEvaluationData },
        calculatedValuationMln: newCalculations,
        activeGlossaryTerm: null,
        activeGateStep: 1,
        isNdaModalOpen: false,
      }));
    },
  };
});

// Browser visibility watcher for Zero-Trace security compliance
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Automatic memory purge on leaving tab to prevent forensics/RAM scraping
      useSessionStore.getState().destroySession('Zero-Trace: Tab Hidden / Focus Lost');
    }
  });
}
